// ═══════════════════════════════════════════════════
//  Overview Tab — Store, Stats, Certificates, Inspections, Store Map
// ═══════════════════════════════════════════════════
import React from "react";
import LocationMap from "../LocationMap";
import {
  ShoppingBag, Building2, MapPin, Mail, Phone, Navigation,
  BadgeCheck, CheckCircle, XCircle, AlertTriangle, Star, Package, Shield,
} from "lucide-react";

import { ScanData, Store } from "./types";
import { fmtAddress } from "./helpers";

interface OverviewTabProps {
  scanData: ScanData;
  store?: Store;
  stores: Store[];
  loadingStores: boolean;
  batchDetails: any;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  scanData,
  store,
  stores,
  loadingStores,
  batchDetails,
}) => {
  const isLevel2 = scanData.level === 2;
  const stats = scanData.stats;
  const consumer = scanData.consumer;

  return (
    <div className="space-y-6">
      {/* Store + Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        {/* ═══ STORE CARD ═══ */}
        {store ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 flex flex-col justify-between h-full">
            <div className="flex items-start gap-4">
              <div className="bg-teal-50 p-3 rounded-xl flex-shrink-0 border border-teal-100">
                <ShoppingBag className="w-6 h-6 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Store Information</h3>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="font-semibold text-gray-800">
                    {store.storeName || `Store ${store.storeId}`}
                  </span>
                  {store.storeType && (
                    <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                      {store.storeType}
                    </span>
                  )}
                </div>

                <div className="space-y-3 text-sm">
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-xs font-medium">Store ID</span>
                      <span className="font-mono text-gray-900 text-xs bg-white px-2 py-0.5 rounded border border-gray-200">
                        {store.storeId}
                      </span>
                    </div>
                  </div>

                  {store.manager && (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="text-xs">
                        <span className="text-gray-500">Manager: </span>
                        <span className="font-medium text-gray-800">{store.manager}</span>
                      </div>
                    </div>
                  )}

                  {store.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <a href={`mailto:${store.email}`} className="text-xs font-medium text-teal-600 hover:text-teal-700 hover:underline">
                        {store.email}
                      </a>
                    </div>
                  )}

                  {(store.storePhone || store.phone) && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <a href={`tel:${store.storePhone || store.phone}`} className="text-xs font-medium text-teal-600 hover:text-teal-700 hover:underline">
                        {store.storePhone || store.phone}
                      </a>
                    </div>
                  )}

                  {fmtAddress(store.address) && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-700 leading-relaxed">{fmtAddress(store.address)}</span>
                    </div>
                  )}

                  {store.gpsValidation && (
                    <div className="bg-teal-50 p-3 rounded-lg border border-teal-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Navigation className="w-3.5 h-3.5 text-teal-600" />
                        <span className="font-medium text-teal-800 text-xs">GPS Validation</span>
                      </div>
                      <div className="text-xs text-teal-700 space-y-0.5">
                        <div>Status: {store.gpsValidation.enabled ? 'Enabled' : 'Disabled'}</div>
                        <div>Radius: {store.gpsValidation.radius}m</div>
                        {store.gpsValidation.strictMode && <div>Mode: Strict</div>}
                      </div>
                    </div>
                  )}

                  {store.latitude && store.longitude && (
                    <div className="rounded-lg overflow-hidden border border-gray-200 mt-2">
                      <LocationMap
                        points={[{ latitude: store.latitude, longitude: store.longitude, label: store.storeName || `Store ${store.storeId}` }]}
                        height="160px"
                        zoom={15}
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {store.latitude && store.longitude && (
                    <>
                      <button
                        onClick={() => window.open(`https://www.google.com/maps?q=${store.latitude},${store.longitude}`, '_blank')}
                        className="bg-teal-50 text-teal-700 border border-teal-200 px-3 py-1.5 rounded-lg hover:bg-teal-100 transition-colors flex items-center gap-1 text-xs font-medium"
                      >
                        <MapPin className="w-3 h-3" /> Open in Maps
                      </button>
                      <button
                        onClick={() => {
                          navigator.geolocation?.getCurrentPosition((pos) => {
                            window.open(`https://www.google.com/maps/dir/${pos.coords.latitude},${pos.coords.longitude}/${store.latitude},${store.longitude}`, '_blank');
                          });
                        }}
                        className="bg-gray-50 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1 text-xs font-medium"
                      >
                        <Navigation className="w-3 h-3" /> Get Directions
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* ═══ STATS PANEL ═══ */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 flex flex-col justify-between h-full">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {isLevel2 ? "Scan Statistics" : "Basic Scan Info"}
          </h3>

          {isLevel2 && stats && consumer ? (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-teal-50 p-3 rounded-xl flex flex-col items-center justify-center border border-teal-100">
                  <div className="text-2xl font-extrabold text-teal-700">{stats.totalScans}</div>
                  <div className="text-xs text-teal-600 mt-0.5">Total Scans</div>
                </div>
                <div className="bg-emerald-50 p-3 rounded-xl flex flex-col items-center justify-center border border-emerald-100">
                  <div className="text-2xl font-extrabold text-emerald-700">{stats.uniqueConsumers || 0}</div>
                  <div className="text-xs text-emerald-600 mt-0.5">Unique Users</div>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl flex flex-col items-center justify-center border border-slate-100">
                  <div className="text-2xl font-extrabold text-slate-700">{stats.trendingScore?.toFixed(1) || "0"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Trending</div>
                </div>
                <div className="bg-amber-50 p-3 rounded-xl flex flex-col items-center justify-center border border-amber-100">
                  <div className="text-2xl font-extrabold text-amber-700">{consumer.pointsEarned}</div>
                  <div className="text-xs text-amber-600 mt-0.5">Points</div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 p-4 rounded-xl border border-teal-100">
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-teal-600" />
                  <div>
                    <h4 className="font-semibold text-teal-900 text-sm">Level: {consumer.loyaltyLevel}</h4>
                    <p className="text-xs text-teal-700">{consumer.totalScans} scans · {consumer.achievements.length} achievements</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-teal-50 p-3 rounded-xl flex flex-col items-center justify-center border border-teal-100">
                <div className="text-2xl font-extrabold text-teal-700">{stats?.totalScans || 0}</div>
                <div className="text-xs text-teal-600 mt-0.5">Scans</div>
              </div>
              <div className="bg-emerald-50 p-3 rounded-xl flex flex-col items-center justify-center border border-emerald-100">
                <div className="text-2xl font-extrabold text-emerald-700">{scanData.verified ? "✓" : "–"}</div>
                <div className="text-xs text-emerald-600 mt-0.5">Verified</div>
              </div>
              <div className="bg-slate-50 p-3 rounded-xl flex flex-col items-center justify-center border border-slate-100">
                <div className="text-2xl font-extrabold text-slate-700">Lv {scanData.level}</div>
                <div className="text-xs text-slate-500 mt-0.5">Access</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ BATCH & WARRANTY INFO ═══ */}
      {(() => {
        // Merge batch info from ALL possible sources
        const bd: any = batchDetails || {};
        const sb: any = scanData?.batch || {};
        const pd: any = (scanData?.product as any) || {};
        const tr: any = scanData?.traceability || {};

        // Flatten — first truthy value wins
        const batchId = bd.batchId || bd.name || bd._id || sb.batchId || tr.batchId || pd.batchId;
        const status = bd.status || sb.status || tr.status;
        const expiryDate = bd.expiryDate || bd.expiry?.expiryDate || sb.expiryDate || pd.expiryDate;
        const isExpired = expiryDate ? new Date(expiryDate) < new Date() : (bd.isExpired || bd.expiry?.isExpired || sb.isExpired);
        const mfg = bd.manufacturing || sb.manufacturing || pd.manufacturing;
        const warranty = bd.warranty || sb.warranty || pd.warranty;
        const rawGrade = bd.qualityGrade || bd.qualityControl?.qualityGrade || sb.qualityGrade || pd.qualityGrade;
        const qualityGrade = rawGrade && rawGrade !== 'N/A' && rawGrade !== 'n/a' ? rawGrade : null;
        // Certifications: may be string[] from controller or object[] from raw batch
        const rawCerts = bd.certifications || bd.qualityControl?.certificates || sb.certifications || pd.certifications || [];
        const certNames: string[] = rawCerts.map((c: any) => typeof c === 'string' ? c : c?.name).filter(Boolean);
        const totalQty = bd.totalQuantity ?? bd.quantity ?? sb.totalQuantity ?? sb.quantity;
        const remainingQty = bd.remainingQuantity ?? sb.remainingQuantity;
        const createdAt = bd.createdAt || bd.created || sb.createdAt;
        const description = bd.description || sb.description;
        const notes = bd.notes || sb.notes;
        const batchLocation = bd.location || sb.location;

        const hasContent = expiryDate || mfg || warranty || qualityGrade || certNames.length > 0 || totalQty != null || description || notes;
        const isRecalled = status === 'recalled' || bd.recall?.isRecalled === true || sb.recall?.isRecalled === true;
        if (!batchId) return null;

        return (
          <div className={`bg-white rounded-2xl shadow-lg border ${isRecalled ? 'border-red-300' : 'border-gray-200'} overflow-hidden`}>
            <div className={`px-6 py-4 border-b flex items-center gap-2 flex-wrap ${isRecalled ? 'border-red-200 bg-red-50' : 'border-gray-100'}`}>
              <Package className={`w-5 h-5 ${isRecalled ? 'text-red-600' : 'text-indigo-600'}`} />
              <h3 className="text-lg font-bold text-gray-900">Batch & Warranty</h3>
              <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-0.5 rounded-full text-xs font-semibold">
                {batchId}
              </span>
              {status && (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                  status === 'active' || status === 'activated' ? 'bg-green-50 text-green-700 border-green-200' :
                  status === 'recalled' ? 'bg-red-50 text-red-700 border-red-200' :
                  status === 'expired' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                  status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  'bg-gray-50 text-gray-600 border-gray-200'
                }`}>
                  {status}
                </span>
              )}
            </div>
            <div className="p-6 space-y-5">

              {/* ⚠️ RECALL ALERT — top priority, always first */}
              {isRecalled && (
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 animate-pulse">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-red-900 font-bold text-base">⚠️ Product Recall Notice</h4>
                      <p className="text-red-700 text-sm mt-1">
                        This batch has been <strong>recalled</strong> by the manufacturer.
                        Please stop using this product immediately and contact the retailer or manufacturer for further instructions.
                      </p>
                      {bd.recall?.reason && (
                        <div className="mt-3 bg-red-100 rounded-lg p-3 border border-red-200">
                          <p className="text-xs font-semibold text-red-800">Recall Reason:</p>
                          <p className="text-sm text-red-700 mt-0.5">{bd.recall.reason}</p>
                        </div>
                      )}
                      {bd.recall?.severity && (
                        <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold border ${
                          bd.recall.severity === 'critical' ? 'bg-red-200 text-red-900 border-red-400' :
                          bd.recall.severity === 'high' ? 'bg-red-100 text-red-800 border-red-300' :
                          bd.recall.severity === 'medium' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                          'bg-yellow-100 text-yellow-800 border-yellow-300'
                        }`}>
                          Severity: {bd.recall.severity.toUpperCase()}
                        </span>
                      )}
                      {bd.recall?.date && (
                        <p className="text-xs text-red-500 mt-2">Recalled on: {new Date(bd.recall.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                      )}
                      {bd.recall?.contactInfo && (
                        <p className="text-xs text-red-600 mt-1">Contact: {bd.recall.contactInfo}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!hasContent && !isRecalled && (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-400">Batch ID registered — no additional details available yet.</p>
                  {createdAt && <p className="text-xs text-gray-300 mt-1">Created: {new Date(createdAt).toLocaleDateString()}</p>}
                </div>
              )}

              {/* Description */}
              {description && (
                <p className="text-sm text-gray-600">{description}</p>
              )}

              {/* Expiry / Warranty Alert */}
              {expiryDate && (
                <div className={`flex items-start gap-3 p-4 rounded-xl border ${
                  isExpired ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isExpired ? 'bg-red-100' : 'bg-emerald-100'
                  }`}>
                    {isExpired
                      ? <XCircle className="w-5 h-5 text-red-600" />
                      : <CheckCircle className="w-5 h-5 text-emerald-600" />
                    }
                  </div>
                  <div>
                    <h4 className={`font-semibold text-sm ${isExpired ? 'text-red-900' : 'text-emerald-900'}`}>
                      {isExpired ? 'Product Expired' : 'Valid Product'}
                    </h4>
                    <p className={`text-xs mt-0.5 ${isExpired ? 'text-red-700' : 'text-emerald-700'}`}>
                      {isExpired ? 'Expiry' : 'Expires'}: {new Date(expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                    {!isExpired && (() => {
                      const days = Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                      return <p className="text-xs text-emerald-600 mt-0.5">{days} days remaining</p>;
                    })()}
                  </div>
                </div>
              )}

              {/* Warranty */}
              {warranty && typeof warranty === 'object' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <h4 className="font-semibold text-blue-900 text-sm">Warranty</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {warranty.type && <div><span className="text-blue-600">Type:</span> <span className="font-medium text-blue-900">{warranty.type}</span></div>}
                    {warranty.duration && <div><span className="text-blue-600">Duration:</span> <span className="font-medium text-blue-900">{warranty.duration}</span></div>}
                    {warranty.coverage && <div className="col-span-2"><span className="text-blue-600">Coverage:</span> <span className="font-medium text-blue-900">{warranty.coverage}</span></div>}
                    {warranty.expiryDate && (
                      <div className="col-span-2">
                        <span className="text-blue-600">Warranty Expires:</span>{' '}
                        <span className={`font-medium ${new Date(warranty.expiryDate) < new Date() ? 'text-red-600' : 'text-blue-900'}`}>
                          {new Date(warranty.expiryDate).toLocaleDateString()}
                          {new Date(warranty.expiryDate) < new Date() && ' (Expired)'}
                        </span>
                      </div>
                    )}
                    {warranty.terms && <div className="col-span-2 text-blue-700 italic">{warranty.terms}</div>}
                  </div>
                </div>
              )}
              {/* Warranty as string */}
              {warranty && typeof warranty === 'string' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <h4 className="font-semibold text-blue-900 text-sm">Warranty</h4>
                  </div>
                  <p className="text-xs text-blue-800 mt-1">{warranty}</p>
                </div>
              )}

              {/* Details Grid */}
              {(qualityGrade || totalQty != null) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {qualityGrade && (
                    <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                      <div className="text-xl font-extrabold text-amber-700">{qualityGrade}</div>
                      <div className="text-xs text-amber-600 mt-0.5">Quality Grade</div>
                    </div>
                  )}
                  {totalQty != null && (
                    <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-100">
                      <div className="text-xl font-extrabold text-indigo-700">{totalQty.toLocaleString()}</div>
                      <div className="text-xs text-indigo-600 mt-0.5">Total Quantity</div>
                    </div>
                  )}
                  {remainingQty != null && (
                    <div className="bg-teal-50 rounded-xl p-3 text-center border border-teal-100">
                      <div className="text-xl font-extrabold text-teal-700">{remainingQty.toLocaleString()}</div>
                      <div className="text-xs text-teal-600 mt-0.5">Remaining</div>
                    </div>
                  )}
                  {totalQty != null && remainingQty != null && (
                    <div className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
                      <div className="text-xl font-extrabold text-slate-700">{Math.round((remainingQty / totalQty) * 100)}%</div>
                      <div className="text-xs text-slate-500 mt-0.5">In Stock</div>
                    </div>
                  )}
                </div>
              )}

              {/* Manufacturing */}
              {mfg && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="font-semibold text-gray-800 text-sm mb-2">Manufacturing Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {mfg.date && <div><span className="text-gray-500">Date:</span> <span className="font-medium text-gray-800">{new Date(mfg.date).toLocaleDateString()}</span></div>}
                    {mfg.location && <div><span className="text-gray-500">Location:</span> <span className="font-medium text-gray-800">{mfg.location}</span></div>}
                    {(mfg.facility || mfg.line) && <div><span className="text-gray-500">Facility/Line:</span> <span className="font-medium text-gray-800">{mfg.facility || mfg.line}</span></div>}
                    {mfg.shift && <div><span className="text-gray-500">Shift:</span> <span className="font-medium text-gray-800">{mfg.shift}</span></div>}
                    {mfg.supervisor && <div><span className="text-gray-500">Supervisor:</span> <span className="font-medium text-gray-800">{mfg.supervisor}</span></div>}
                  </div>
                </div>
              )}

              {/* Certifications (from batch, not uploaded files) */}
              {certNames.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-2">Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {certNames.map((cert: string, idx: number) => (
                      <span key={idx} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-full text-xs font-semibold">
                        <BadgeCheck className="w-3.5 h-3.5" /> {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ═══ STORE MAP ═══ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-teal-600" />
          <h3 className="text-lg font-bold text-gray-900">Store Locations</h3>
          {loadingStores && <span className="text-sm text-gray-400 animate-pulse">Loading...</span>}
          {stores.length > 0 && (
            <span className="bg-teal-50 text-teal-700 border border-teal-200 px-2.5 py-0.5 rounded-full text-xs font-semibold">
              {stores.length} Store{stores.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div>
          <LocationMap
            points={
              stores.length > 0
                ? stores.map((s: any) => ({ latitude: s.latitude, longitude: s.longitude, label: `${s.storeName || s.storeId} — ${s.storeType || "store"}` }))
                : store?.latitude && store?.longitude
                ? [{ latitude: store.latitude, longitude: store.longitude, label: store.storeName || "Store Location" }]
                : [{ latitude: 37.3352, longitude: -121.8811, label: "Default Location (San Jose HQ)" }]
            }
            height="350px"
            zoom={stores.length === 1 ? 13 : 5}
          />
        </div>
        {stores.length > 0 && (
          <div className="p-6 border-t border-gray-100">
            <h4 className="font-semibold text-gray-800 mb-3 text-sm">Store Directory</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {stores.map((storeItem, idx) => (
                <div key={storeItem.storeId || idx} className="flex justify-between items-center py-2.5 px-4 bg-gray-50 rounded-lg text-sm border border-gray-100 hover:border-teal-200 transition-colors">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-teal-500" />
                    <span className="font-medium text-gray-900">{storeItem.storeName || storeItem.storeId}</span>
                    {storeItem.storeType && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{storeItem.storeType}</span>}
                  </div>
                  <span className="text-xs text-gray-400 font-mono">
                    {storeItem.latitude && storeItem.longitude ? `${storeItem.latitude.toFixed(4)}, ${storeItem.longitude.toFixed(4)}` : "No coords"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewTab;